<script>
    import { Modal, ModalBody, ModalHeader, ModalFooter } from "sveltestrap";
    import alertify from "alertifyjs";
    import { getProviderTemplate, buildKeyFromPattern, templateNeedsCurrency, REVERSE_API_PROVIDERS } from "../../../../js/providersTemplates.js";
    import { ICONS } from "../../../../js/icons.js";
    import backend from "../../../../server";

    export let open = false;
    export let onClose = () => {};
    export let provider = null;
    export let platform = null;
    export let mode = 'create';
    export let initialValues = null;
    export let activeEnv = "prod"; // nuevo prop


    let saveTarget = activeEnv;
    let platformInput = "";
    let currencyInput = "";
    let values = {};
    let showAdvanced = false;
    let wasOpen = false;
    let hydrated = false;
    let original = null;
    let sectionInt = Number(
        (initialValues?.env ?? initialValues?.section ?? provider?.env ?? 1)
    );

    $: template = getProviderTemplate(provider && provider.code);
    $: needsCurrency = templateNeedsCurrency(template);

    const isReverseApiProvider = () => {
        return REVERSE_API_PROVIDERS.has(String(provider?.code || "").toLowerCase());
    };

    const isReadOnlyField = (pattern) => {
        if (!isReverseApiProvider()) return false;
        return pattern.includes(".auth") || pattern.includes(".private_key");
    };
    // adm/Imperium?
    const isImperium = () => {
        const code = String(provider?.code || "").toLowerCase();
        const name = String(provider?.name || "").toLowerCase();
        return code === "adm" || name.includes("imperium");
    };

    $: if (open && platform?.code && !platformInput) {
        platformInput = String(platform.code);
    }

    $: if (open && !hydrated) {
        if (mode === 'edit' && initialValues) {
            platformInput = initialValues.platform || (platform?.code ? String(platform.code) : '');
            currencyInput = (initialValues.currency || '').toUpperCase();
            sectionInt = Number(initialValues.env ?? initialValues.section ?? sectionInt);
            const out = {};
            for (const f of (template?.fields || [])) {
                const k = f.pattern;
                out[k] = (initialValues.values && initialValues.values[k]) || '';
            }
            values = out;
            original = { platform: platformInput, currency: currencyInput, values: { ...out } };
        } else {
            platformInput = (platform?.code ? String(platform.code) : '');
            currencyInput = '';
            values = {};
            sectionInt = Number(initialValues?.env ?? initialValues?.section ?? provider?.env ?? 1);
            original = { platform: platformInput, currency: currencyInput, values: {} };
        }
        hydrated = true;
    }

    $: if (!open && wasOpen) {
        resetForm();
        platformInput = '';
        hydrated = false;
        original = null;
    }
    $: wasOpen = open;

    // ---- Helpers ----
    const trim = (x) => String(x ?? '').trim();
    const trimU = (x) => String(x ?? '').trim().toUpperCase();
    const isCurrencyField = (pattern) => /\$\{currency\}/i.test(pattern);
    const isSharedField   = (pattern) => !isCurrencyField(pattern);

    const validate = () => {
        if (!provider) { alertify.error("Selecciona un proveedor"); return false; }
        if (!platformInput.trim()) { alertify.error("Completa el campo Platform"); return false; }
        if (needsCurrency && !currencyInput.trim()) { alertify.error("Completa el campo Currency"); return false; }
        for (const field of (template?.fields || [])) {
            const v = values[field.pattern] && values[field.pattern].trim();
            if (!v) { alertify.error(`Completa el campo: ${field.label || field.pattern}`); return false; }
        }
        return true;
    };

    const changedFieldPatterns = () => {
        if (!original) return [];
        const diffs = [];
        for (const f of (template?.fields || [])) {
            const k = f.pattern;
            if (trim(values[k]) !== trim(original.values?.[k])) diffs.push(k);
        }
        return diffs;
    };
    const platformChanged = () => trim(platformInput) !== trim(original?.platform);
    const currencyChanged = () => needsCurrency ? trimU(currencyInput) !== trimU(original?.currency) : false;

    const buildPayloadsFrom = (patterns) => {
        const p = trim(platformInput);
        const c = needsCurrency ? trimU(currencyInput) : "";
        const provCode = String(provider?.code || "").toLowerCase(); // agregar esto
        const arr = [];
        for (const f of (template?.fields || [])) {
            if (!patterns.includes(f.pattern)) continue;
            const secretName = buildKeyFromPattern(f.pattern, { platform: p, currency: c, providerCode: provCode});
            const secretValue = trim(values[f.pattern]);
            arr.push({ secretName, secretValue, origin: "public" });
        }
        return arr;
    };

    const handleSave = async () => {
        if (!validate()) return;
        const sendAll = platformChanged() || currencyChanged();
        const patterns = sendAll
            ? (template?.fields || []).map(f => f.pattern)
            : changedFieldPatterns();
        if (!patterns.length) { alertify.message("No hay cambios para guardar"); return; }
        const payloads = buildPayloadsFrom(patterns);
        try {
            await Promise.all(payloads.map(p => backend.client.saveCredentials(p, sectionInt)));
            alertify.success("Cambios guardados");
            onClose && onClose();
        } catch (e) {
            console.error(e);
            alertify.error("Error al guardar");
        }
    };

    let deleteAllImperium = false;

    const buildNamesDeleteHallOnly = () => {
        if (!needsCurrency || !trimU(currencyInput)) return [];
        const p = trim(platformInput);
        const c = trimU(currencyInput);
        return (template?.fields || [])
            .filter(f => isCurrencyField(f.pattern))
            .map(f => buildKeyFromPattern(f.pattern, { platform: p, currency: c }));
    };

    const buildNamesDeleteAllBlock = () => {
        const p = trim(platformInput);
        const out = [];
        for (const f of (template?.fields || [])) {
            if (isSharedField(f.pattern)) {
                out.push(buildKeyFromPattern(f.pattern, { platform: p, currency: "" }));
            }
        }
        const currencies = Array.isArray(initialValues?.currencies) ? initialValues.currencies : [];
        for (const cur of currencies) {
            for (const f of (template?.fields || [])) {
                if (isCurrencyField(f.pattern)) {
                    out.push(buildKeyFromPattern(f.pattern, { platform: p, currency: String(cur || "").toUpperCase() }));
                }
            }
        }
        return out;
    };

    const handleDelete = async () => {
        const pf = provider?.name || provider?.code || "Proveedor";
        const pl = platform?.namesite || platformInput || "Plataforma";
        if (!isImperium()) {
            const p = trim(platformInput);
            const c = needsCurrency ? trimU(currencyInput) : "";
            const names = (template?.fields || []).map(f =>
                buildKeyFromPattern(f.pattern, { platform: p, currency: c })
            );
            if (!names.length) { alertify.message("No hay claves para eliminar"); return; }
            const payloads = names.map(keyId => ({ keyId, section: sectionInt }));
            return alertify.confirm(
                "Cuidado",
                `¿Eliminar credenciales de ${pf} en ${pl}${needsCurrency ? ` (moneda ${c || "—"})` : ""}?`,
                async () => {
                    try {
                        await Promise.all(payloads.map(p => backend.client.deleteCredentials(p)));
                        alertify.success("Credenciales eliminadas");
                        onClose && onClose();
                    } catch (e) {
                        console.error(e);
                        alertify.error("No se pudieron eliminar las credenciales");
                    }
                },
                () => {}
            );
        }
        const idChk = `imperium-delall-${Date.now()}`;
        alertify.confirm(
            "Eliminar en Imperium",
            `
        <div class="form-check" style="text-align:left">
           ${needsCurrency ? `<div><label>Solo se eliminará la moneda actual</label></div>` : ``}
          <input id="${idChk}" type="checkbox" class="form-check-input" ${deleteAllImperium ? "checked" : ""}/>
          <small for="${idChk}" class="form-check-label">
            Eliminar todos los valores
          </small>
        </div>
      `,
            async () => {
                deleteAllImperium = !!document.getElementById(idChk)?.checked;
                let names = [];
                if (deleteAllImperium) {
                    names = buildNamesDeleteAllBlock();
                } else {
                    if (needsCurrency && !trimU(currencyInput)) {
                        alertify.error("Indica la moneda");
                        return;
                    }
                    names = buildNamesDeleteHallOnly();
                }
                if (!names.length) { alertify.message("No hay claves para eliminar"); return; }
                const payloads = names.map(keyId => ({ keyId, section: sectionInt }));

                try {
                    await Promise.all(payloads.map(p => backend.client.deleteCredentials(p)));
                    alertify.success(deleteAllImperium ? "Bloque completo eliminado" : "HALL_ID eliminado");
                    onClose && onClose();
                } catch (e) {
                    console.error(e);
                    alertify.error("No se pudieron eliminar las credenciales");
                }
            },
            () => {}
        );
    };
    const resetForm = () => {
        values = {};
        currencyInput = "";
        showAdvanced = false;
        deleteAllImperium = false;
    };
</script>

<Modal isOpen={open} toggle={onClose} size="lg" backdrop class="upload-modal">
    <ModalHeader toggle={onClose}>
        <div class="w-100 d-flex justify-content-between align-items-center">
            <span class="h5 mb-0">{mode === 'edit' ? 'Editar credenciales' : 'Agregar credenciales'}</span>
            <button type="button" class="eye-btn ms-2" on:click={() => (showAdvanced = !showAdvanced)}>
                {@html ICONS.eye}
            </button>
        </div>
    </ModalHeader>

    <ModalBody>
        <div class="mb-3 medium">
            <div>
                <strong>Plataforma:</strong>
                {platform?.namesite || "—"}
                {#if showAdvanced && platformInput}
                    <span class="pattern-hint">({platformInput})</span>
                {/if}
            </div>
            <div>
                <strong>Proveedor:</strong>
                {provider?.name || "—"}
                {#if showAdvanced && provider?.code}
                    <span class="pattern-hint">({provider.code})</span>
                {/if}
            </div>
        </div>

        {#if needsCurrency}
            <div class="mb-2">
                <label for="curr" class="form-label">Moneda</label>
                <input
                        id="curr"
                        class="form-control"
                        value={currencyInput}
                        on:input={(e) => { currencyInput = e.target.value.toUpperCase(); }}
                        on:blur={() => { currencyInput = (currencyInput || '').trim().toUpperCase(); }}
                        placeholder="Ingresar la moneda"
                />
            </div>
        {/if}

        {#each template.fields as field}
            <div class="mb-2">
                <label for="place" class="form-label label-inline">
                    <span>{field.label && field.label.trim() ? field.label : field.pattern}</span>
                    {#if showAdvanced}
                        <span class="pattern-hint">{field.pattern}</span>
                    {/if}
                </label>
                <input
                        id="place"
                        class="form-control"
                        bind:value={values[field.pattern]}
                        placeholder={field.placeholder || "Valor"}
                        disabled={isReadOnlyField(field.pattern)}
                />
                {#if isReadOnlyField(field.pattern)}
                    <small class="text-muted">Este campo es de solo lectura</small>
                {/if}
            </div>
        {/each}
    </ModalBody>

    <ModalFooter class="d-flex align-items-center">
        {#if mode === 'edit'}
            <button class="btn btn-outline-danger me-auto" on:click={handleDelete}>
                {@html ICONS.delete} Eliminar
            </button>
        {/if}
        <div class="ms-auto d-flex gap-2">
            {#if activeEnv === 'test'}
                <div class="d-flex align-items-center gap-1">
                    <span class="small fw-semibold">Guardar en:</span>
                    <select class="form-select form-select-sm" bind:value={saveTarget}>
                        <option value="test">Test</option>
                        <option value="prod">Producción</option>
                    </select>
                </div>
            {/if}
            <button class="btn btn-secondary" on:click={onClose}>Cancelar</button>
            <button class="btn btn-primary" on:click={handleSave}>Guardar</button>
        </div>
    </ModalFooter>
</Modal>

<style>
    .form-label { font-weight: 600; }
    .label-inline{ display:inline-flex; align-items:baseline; gap:.4rem; }
    .pattern-hint{ color:#dc3545; font-size:0.95em; line-height:1; }
    .eye-btn { border: 0; background: transparent; padding: .2rem .35rem; line-height: 1; display: inline-flex; align-items: center; cursor: pointer; }
    .eye-btn:hover { background: rgba(0,0,0,.04); border-radius: .375rem; }
</style>
